<template>
   <div class="card">
      <div class="card-body m-2">
         <div class="row p-2 align-items-center">

            <!-- Search + Report Button -->
            <div class="col-12 col-lg-4 mb-2 mb-lg-0 d-flex gap-2">
               <input v-model="search" type="text" class="form-control form-control-sm"
                  placeholder="&#xF002; Search reported websites..."
                  style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif, 'Font Awesome 6 Free'; font-weight: 900;"
                  @keyup="onSearch()" />
            </div>

            <div class="col-12 col-lg-auto d-flex mb-2 mb-lg-0 gap-2">
               <button type="button" class="fw-bold btn text-light btn-sm"
                  style="font-size: 14px; height: 40px; border-radius: 8px !important; padding: 0 1.5rem; white-space: nowrap; background: #e65100;"
                  @click="showReportModal = true">
                  <i class="fa-solid fa-flag me-1"></i> Report A Website
               </button>
            </div>

            <!-- Filters + Pagination -->
            <div class="col-12 col-lg d-flex flex-wrap justify-content-lg-end align-items-center">

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Status:</span>
                  <select v-model="filterStatusBy" class="form-select shadow-none filterOptions bordered-select">
                     <option v-for="s in filterStatusList" :value="s.value" :key="s.value">{{ s.text }}</option>
                  </select>
               </div>

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Sort by:</span>
                  <select v-model="sortBy" class="form-select shadow-none filterOptions bordered-select">
                     <option v-for="s in sortByList" :value="s.value" :key="s.value">{{ s.text }}</option>
                  </select>
               </div>

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Rows per page:</span>
                  <select v-model="pagination.pageSize"
                     class="form-select form-select-sm shadow-none filterOptions bordered-select" style="width: auto;">
                     <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}</option>
                  </select>
               </div>

               <div class="pagination" v-if="pagination.totalItems > 0">
                  <button @click="previousPage()" :disabled="pagination.page == 1" class="pagination-arrow">
                     <i class="fa fa-chevron-left"></i>
                  </button>
                  <span class="pagination-label">
                     <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
                  </span>
                  <button @click="nextPage()"
                     :disabled="pagination.page == pagination.totalPages || pagination.totalItems == 0"
                     class="pagination-arrow">
                     <i class="fa fa-chevron-right"></i>
                  </button>
               </div>

            </div>
         </div>

         <!-- Table -->
         <div class="row p-2">
            <table class="table align-middle mainTable">
               <thead class="align-middle">
                  <tr>
                     <th scope="col" v-for="header in headers" :key="header.value">{{ header.text }}</th>
                  </tr>
               </thead>

               <tbody v-if="pagination.totalItems <= 0">
                  <tr>
                     <td :colspan="headers.length">No records to display.</td>
                  </tr>
               </tbody>

               <tbody v-else>
                  <tr v-for="(rw, index) in reportedWebsites" :key="index">
                     <td>
                        <span class="status-pill" :class="getStatusClass(rw)">
                           <i :class="getStatusIcon(rw)" class="me-1"></i>
                           {{ getStatusText(rw) }}
                        </span>
                     </td>
                     <td>{{ rw.account ? rw.account.account_name : '—' }}</td>
                     <td style="font-family: monospace; font-size: 12px;" :title="trimQuestionMark(rw.website)">
                        {{ truncateText(trimQuestionMark(rw.website), 45) }}
                     </td>
                     <td>
                        <span class="category-text">{{ formatCategory(rw.category) }}</span>
                     </td>
                     <td>
                        <span class="request-pill" :class="getReportTypeClass(rw.report_type)">
                           {{ formatReportType(rw.report_type) }}
                        </span>
                     </td>
                     <td style="font-family: monospace; font-size: 11px;" :title="rw.api_key">
                        {{ truncateText(rw.api_key, 20) }}
                     </td>
                     <td class="date-text">{{ formatDate(rw.date_created) }}</td>
                     <td class="date-text">
                        {{ (rw.is_marked > 0 && rw.date_actioned && !rw.date_actioned.startsWith('0001-01-01')) ? formatDate(rw.date_actioned) : '—' }}
                     </td>
                  </tr>
               </tbody>
            </table>
         </div>

      </div>
   </div>

   <!-- Report Modal -->
   <div class="modal-backdrop-custom" v-if="showReportModal" @click.self="showReportModal = false">
      <div class="modal-content-custom">
         <button @click="showReportModal = false; inputWebsite = ''" class="close-button-custom">&times;</button>
         <h5 class="modal-title-custom">
            <i class="fa-solid fa-flag me-2" style="color: #e65100;"></i>
            Report A Website
         </h5>
         <hr>
         <div class="modal-body-custom">
            <label class="filter-label">Website URL</label>
            <input type="text" class="form-control form-control-sm"
               style="border-radius: 8px;"
               placeholder="e.g. example.com"
               v-model="inputWebsite" v-on:keyup.enter="reportWebsite()">
            <small class="text-muted mt-1 d-block">Enter the domain you want to report (e.g. example.com). Do not include http:// or https://.</small>
         </div>
         <div class="modal-footer-custom">
            <button type="button" class="btn btn-sm btn-modal-cancel" @click="showReportModal = false; inputWebsite = ''">Cancel</button>
            <button type="button" class="btn btn-sm text-light" style="background: #e65100; border-radius: 8px;"
               @click="reportWebsite()">
               <i class="fa-solid fa-flag me-1"></i> Report
            </button>
         </div>
      </div>
   </div>
</template>

<script>
import moment from 'moment'
import { debounce, extractErrorMessage } from '@/plugins/helper.js'

export default {

   data() {
      return {
         showReportModal: false,
         inputWebsite: "",
         headers: [
            { text: "Status", value: "status" },
            { text: "Account", value: "accountName" },
            { text: "Domain", value: "domain" },
            { text: "Category", value: "category" },
            { text: "Request", value: "reportType" },
            { text: "Extension Key", value: "extensionKey" },
            { text: "Reported", value: "dateAndTime" },
            { text: "Actioned", value: "dateActioned" },
         ],
         search: "",
         reportedWebsites: [],
         debounceFn: null,
         paginationData: {},
         pagination: {
            pageSize: 50, page: 1, totalItems: 0, totalPages: 1,
         },
         rowsPerPageList: [
            { text: "50", value: 50 },
            { text: "75", value: 75 },
            { text: "100", value: 100 },
         ],
         filterStatusList: [
            { text: "All Status", value: "all" },
            { text: "Pending", value: "pending" },
            { text: "Safe", value: "safe" },
            { text: "Unsafe", value: "unsafe" },
         ],
         filterStatusBy: "all",
         sortByList: [
            { text: "Date: Newest", value: "dateCreated_desc" },
            { text: "Date: Oldest", value: "dateCreated_asc" },
         ],
         sortBy: "dateCreated_desc",
         sorting: { field: "dateCreated", order: "desc" },
      }
   },

   computed: {
      paginationLabel() {
         var start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems)
         var end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems)
         return start + " - " + end
      },
      currentPage() { return this.pagination.page },
      rowsPerPage() { return this.pagination.pageSize },
      filterByStatus() { return this.filterStatusBy },
      sortItems() { return this.sortBy },
   },

   watch: {
      currentPage() { this.getReportedWebsites() },
      rowsPerPage() { this.pagination.page = 1; this.getReportedWebsites() },
      filterByStatus() { this.pagination.page = 1; this.getReportedWebsites() },
      sortItems() {
         if (this.sortBy.includes("_")) {
            let sort = this.sortBy.split("_")
            this.sorting.field = sort[0]
            this.sorting.order = sort[1]
         } else {
            this.sorting.field = this.sortBy
            this.sorting.order = ""
         }
         this.getReportedWebsites()
      },
   },

   created() {
      let sort = this.sortBy.split("_")
      this.sorting.field = sort[0]
      this.sorting.order = sort[1]
      this.debounceFn = debounce(() => this.getReportedWebsites(), 500)
      this.getReportedWebsites()
   },

   methods: {
      formatDate(date) { return moment(date).format("DD-MMM-YYYY, hh:mm A") },

      formatReportType(type) {
         if (type === 1) return 'Allow';
         if (type === 0) return 'Block';
         return 'N/A';
      },

      getReportTypeClass(type) {
         if (type === 1) return 'request-allow';
         if (type === 0) return 'request-block';
         return 'request-default';
      },

      formatCategory(value) {
         if (!value || value === 'NONE' || value === 'null') return 'N/A';
         return value;
      },

      getStatusClass(website) {
         if (website.is_marked === 0) return 'status-pending';
         if (website.is_safe >= 1) return 'status-safe';
         return 'status-unsafe';
      },

      getStatusText(website) {
         if (website.is_marked === 0) return 'Pending';
         if (website.is_safe >= 1) return 'Safe';
         return 'Unsafe';
      },

      getStatusIcon(website) {
         if (website.is_marked === 0) return 'fa-solid fa-clock';
         if (website.is_safe >= 1) return 'fa-solid fa-circle-check';
         return 'fa-solid fa-circle-xmark';
      },

      truncateText(text, limit) {
         if (!text) return '';
         return text.length <= limit ? text : text.slice(0, limit) + '...';
      },

      trimQuestionMark(text) {
         if (!text) return '';
         let index = text.indexOf('?');
         return index > 0 ? text.substring(0, index) : text;
      },

      isValidDomain(domain) {
         const domainPattern = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
         return domainPattern.test(domain);
      },

      previousPage() { this.pagination.page > 1 ? this.pagination.page -= 1 : '' },
      nextPage() { this.pagination.totalPages > this.pagination.page ? this.pagination.page += 1 : '' },
      onSearch() { this.pagination.page = 1; this.debounceFn() },

      async reportWebsite() {
         try {
            const domain = this.inputWebsite ? this.inputWebsite.trim().toLowerCase() : ''
            if (!domain || !this.isValidDomain(domain)) {
               this.$toast.error("Please provide a valid website domain")
               return
            }
            const payload = { website: domain }
            const resp = await this.$axios.post("analytics/" + this.$store.state.auth.organisation_uuid + "/add-report-website", payload)
            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message)
               this.getReportedWebsites()
            }
            this.inputWebsite = ""
            this.showReportModal = false
         } catch (error) {
            console.log(error)
            this.$toast.error(extractErrorMessage(error, "Failed to submit reported website."))
            this.inputWebsite = ""
         }
      },

      async getReportedWebsites() {
         let apiPath = "analytics/" + this.$store.state.auth.organisation_uuid + "/get-account-reported-websites?page_size=" + this.pagination.pageSize + "&page=" + this.pagination.page

         if (this.filterStatusBy !== 'all') {
            apiPath += "&status=" + this.filterStatusBy
         }
         if (this.sorting.order != "") {
            apiPath += "&order_by=" + this.sorting.order
            apiPath += "&field=" + this.sorting.field
         }
         if (this.search !== "" || this.search !== null) {
            apiPath += "&search=" + this.search
         }

         await this.$axios.get(apiPath)
            .then((response) => {
               if (response.data.account_reported_websites === "No Record Found")
                  this.reportedWebsites = []
               else {
                  this.reportedWebsites = response.data.account_reported_websites
               }
               this.paginationData = response.data.pagination
               this.pagination.totalItems = this.paginationData.total_count
               this.pagination.totalPages = Math.ceil(this.paginationData.total_count / this.pagination.pageSize)
            })
            .catch((error) => {
               console.log(error)
               this.$toast.error(extractErrorMessage(error, "Failed to load reported websites."))
            })
      },
   },
}
</script>

<style scoped>
.card { border: none; }

.date-text { font-size: 13px; color: #6c757d; white-space: nowrap; }
.category-text { font-size: 13px; color: #435666; }

/* Status pills */
.status-pill {
   padding: 4px 10px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 600;
   display: inline-flex;
   align-items: center;
   white-space: nowrap;
}
.status-pending { background: #fff8e1; color: #e6a100; }
.status-safe { background: #e6f9ee; color: #00873c; }
.status-unsafe { background: #fde8ea; color: #c62828; }

/* Request type pills */
.request-pill {
   padding: 3px 8px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 600;
   display: inline-flex;
   align-items: center;
   white-space: nowrap;
}
.request-allow { background: #e6f9ee; color: #00873c; }
.request-block { background: #fde8ea; color: #c62828; }
.request-default { background: #f1f3f5; color: #6c757d; }

/* Modal */
.modal-backdrop-custom {
   position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
   background-color: rgba(0,0,0,0.5); display: flex;
   justify-content: center; align-items: center; z-index: 1050;
}
.modal-content-custom {
   position: relative; background: #fff; padding: 2rem;
   border-radius: 14px; width: 100%; max-width: 480px;
   box-shadow: 0 8px 30px rgba(0,0,0,0.2);
}
.modal-title-custom { font-weight: 700; font-size: 1.1rem; color: #435666; margin-bottom: 0; }
.modal-content-custom hr { margin: 0.75rem 0 1rem; border-color: #e9ecef; }
.close-button-custom {
   position: absolute; top: 0.75rem; right: 1rem;
   font-size: 1.8rem; font-weight: bold; line-height: 1;
   color: #adb5bd; background: none; border: none; cursor: pointer;
}
.close-button-custom:hover { color: #435666; }
.modal-body-custom { font-size: 14px; color: #435666; }
.filter-label {
   font-size: 11px; font-weight: 600; color: #6c757d;
   text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px;
   display: block;
}
.modal-footer-custom { margin-top: 1rem; display: flex; justify-content: flex-end; gap: 8px; }
.btn-modal-cancel { background: #e9ecef; border: none; border-radius: 8px; color: #435666; }
</style>